import { WidgetType } from 'modules/dashboard/types/widgets/widget.enums';
import { ChartProps } from '../../../../../utils/chart/ChartWidget.tsx';
import { transactionData } from 'stores/mockData';
import { ICONS_ENUM } from '@sanlam-glacier/app-common/components';
import { HelpHeadingText, HelpSubText } from './helpContent';
import { BreakpointKey } from '../../../../../../../providers/breakpoint/BreakpointProvider.tsx';
import { WidgetProps } from '../../../widget/Widget';
import { TableProps, TableType } from 'modules/dashboard/hooks/TableRenderer';

interface LayoutValues {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type WidgetLayoutEntry = {
  layouts: Partial<Record<BreakpointKey, LayoutValues>>;
  widgetProps: WidgetProps;
  chartProps: ChartProps | null;
  tableProps: TableProps | null;
};

export const enum WidgetLabels {
  personalDetails = 'Personal details',
  latestTransactions = 'Latest transactions',
  investmentBreakdown = 'Investment breakdown',
  bankAccounts = 'Bank accounts',
  portfolioSummary = 'Portfolio summary',
  beneficiaries = 'Beneficiaries',
  importantDates = 'Important dates',
  hotlinks = 'Hot links'
}

export const clientLayoutMap: Record<WidgetLabels, WidgetLayoutEntry> = {
  [WidgetLabels.personalDetails]: {
    layouts: {
      lg: { x: 0, y: 0, w: 5, h: 4 },
      md: { x: 0, y: 0, w: 6, h: 4 },
      sm: { x: 0, y: 0, w: 7, h: 4 }
    },
    widgetProps: {
      widgetConfig: {
        type: WidgetType.personalDetails,
        heading: WidgetLabels.personalDetails,
        helpHeadingText: HelpHeadingText.PersonalDetailsNatural,
        helpSubText: HelpSubText.PersonalDetailsNatural,
        customActionIcon: ICONS_ENUM.CONTENT_CREATE,
        hideMode: 'content',
        isHidable: true,
        hasProperties: true,
        isScrollable: true,
        isEditButton: true
      }
    },
    chartProps: null,
    tableProps: null
  },
  [WidgetLabels.latestTransactions]: {
    layouts: {
      lg: { x: 0, y: 8, w: 9, h: 4.2 },
      md: { x: 0, y: 12, w: 9, h: 4.2 },
      sm: { x: 0, y: 16, w: 7, h: 4 }
    },
    // TODO: Replace with latestTransactionsWidget
    widgetProps: {
      widgetConfig: {
        type: WidgetType.table,
        heading: WidgetLabels.latestTransactions,
        helpHeadingText: HelpHeadingText.LatestTransactions,
        helpSubText: HelpSubText.LatestTransactions,
        isHidable: false,
        hasProperties: false,
        isScrollable: false
      }
    },
    chartProps: null,
    tableProps: {
      tableType: TableType.latestTransactions,
      data: transactionData
    }
  },
  [WidgetLabels.investmentBreakdown]: {
    layouts: {
      lg: { x: 6, y: 4, w: 6, h: 4 },
      md: { x: 0, y: 8, w: 6, h: 4 },
      sm: { x: 0, y: 12, w: 7, h: 4 }
    },
    // TODO: Replace with InvestmentBreakdown Widget
    widgetProps: {
      widgetConfig: {
        type: WidgetType.investmentBreakdown,
        heading: WidgetLabels.investmentBreakdown,
        helpHeadingText: HelpHeadingText.InvestmentBreakdown,
        helpSubText: HelpSubText.InvestmentBreakdown,
        isHidable: true,
        hasProperties: false,
        isScrollable: false
      }
    },
    chartProps: null,
    tableProps: null
  },
  [WidgetLabels.bankAccounts]: {
    layouts: {
      lg: { x: 0, y: 4, w: 3, h: 4 },
      md: { x: 4, y: 4, w: 3, h: 4 },
      sm: { x: 0, y: 8, w: 4, h: 4 }
    },
    // TODO: Replace with BankAccountsWidget
    widgetProps: {
      widgetConfig: {
        type: WidgetType.bankAccounts,
        heading: WidgetLabels.bankAccounts,
        helpHeadingText: HelpHeadingText.BankAccounts,
        helpSubText: HelpSubText.BankAccounts,
        customActionIcon: ICONS_ENUM.CONTENT_CREATE,
        isHidable: true,
        hasProperties: true,
        isScrollable: true
      }
    },
    chartProps: null,
    tableProps: null
  },
  [WidgetLabels.portfolioSummary]: {
    layouts: {
      lg: { x: 5, y: 0, w: 5, h: 4 },
      md: { x: 0, y: 4, w: 4, h: 4 },
      sm: { x: 0, y: 4, w: 5, h: 4 }
    },
    widgetProps: {
      widgetConfig: {
        type: WidgetType.portfolioSummary,
        heading: WidgetLabels.portfolioSummary,
        helpHeadingText: HelpHeadingText.PortfolioSummary,
        helpSubText: HelpSubText.PortfolioSummary,
        hideMoreButton: true,
        isHidable: true,
        hasProperties: true,
        isScrollable: false
      }
    },
    chartProps: null,
    tableProps: null
  },
  [WidgetLabels.beneficiaries]: {
    layouts: {
      lg: { x: 3, y: 4, w: 3, h: 4 },
      md: { x: 6, y: 8, w: 3, h: 4 },
      sm: { x: 4, y: 8, w: 3, h: 4 }
    },
    // TODO: Replace with BeneficiariesWidget
    widgetProps: {
      widgetConfig: {
        type: WidgetType.beneficiaries,
        heading: WidgetLabels.beneficiaries,
        helpHeadingText: HelpHeadingText.Beneficiaries,
        helpSubText: HelpSubText.Beneficiaries,
        isHidable: false,
        hasProperties: false,
        isScrollable: true
      }
    },
    chartProps: null,
    tableProps: null
  },
  [WidgetLabels.importantDates]: {
    layouts: {
      lg: { x: 9, y: 8, w: 3, h: 4.2 },
      md: { x: 6, y: 0, w: 3, h: 4 }
      // no important dates widget for sm size screen
    },
    // TODO: Replace with ImportantDatesWidget
    widgetProps: {
      widgetConfig: {
        type: WidgetType.events,
        heading: WidgetLabels.importantDates,
        helpHeadingText: HelpHeadingText.ImportantDates,
        helpSubText: HelpSubText.ImportantDates,
        isHidable: false,
        hasProperties: true,
        hideMoreButton: true,
        customActionIcon: undefined,
        isScrollable: false
      }
    },
    chartProps: null,
    tableProps: null
  },
  [WidgetLabels.hotlinks]: {
    layouts: {
      lg: { x: 10, y: 0, w: 2, h: 4 },
      md: { x: 7, y: 4, w: 2, h: 4 },
      sm: { x: 5, y: 4, w: 2, h: 4 }
    },
    widgetProps: {
      widgetConfig: {
        type: WidgetType.hotlinks,
        heading: WidgetLabels.hotlinks,
        removeElements: true,
        isScrollable: true
      }
    },
    chartProps: null,
    tableProps: null
  }
};
